import { useMemo } from "react";
import { canonicalPersonalNumberKey } from "../utils/personnelDisplay";

/**
 * Check if item is a serial item
 */
const isSerialItem = (item) => {
  return item?.serialNumber && item.serialNumber.trim() !== "";
};

function assignerMatchesChild(assignedBy, childPersonalNumber) {
  if (childPersonalNumber == null || assignedBy == null) return false;
  return (
    canonicalPersonalNumberKey(assignedBy) ===
    canonicalPersonalNumberKey(childPersonalNumber)
  );
}

/** `assignments.first` entry for this child (מ.א. key format–tolerant). */
function getFirstAssignmentEntryForChild(item, childPersonalNumber) {
  if (!item?.assignments?.first || !childPersonalNumber) return null;
  const first = item.assignments.first;
  const t = canonicalPersonalNumberKey(childPersonalNumber);
  for (const k of Object.keys(first)) {
    if (canonicalPersonalNumberKey(k) === t) return first[k];
  }
  return null;
}

function getOwnSecondAssignments(item, childPersonalNumber) {
  const sec = item.assignments?.second;
  if (!sec || !childPersonalNumber) return [];
  return Object.values(sec).filter((a) =>
    assignerMatchesChild(a?.assignedBy, childPersonalNumber),
  );
}

/**
 * Calculate assigned quantity for an inventory item
 * For non-serial items: use availableQuantity field (quantity - availableQuantity)
 * For serial items: calculate from assignments or use status
 * @param {Object} item - Inventory item
 * @param {string} userRole - admin | parentsUser | childUser | childUserUnit (unit-wide inventory child)
 * @param {string} childUserPersonalNumber - Personal number of childUser (if applicable)
 * @returns {number} Total assigned quantity
 */
const calculateAssignedQuantity = (
  item,
  userRole = "admin",
  childUserPersonalNumber = null
) => {
  if (!item) return 0;

  const isChildUser = userRole === "childUser";
  const isSerial = isSerialItem(item);

  // For childUser: calculate based on their own entries in assignments.second
  if (isChildUser && childUserPersonalNumber) {
    const firstAssignment = item.assignments?.first?.[childUserPersonalNumber];
    if (!firstAssignment) return 0;

    const secondAssignments = item.assignments?.second;
    if (!secondAssignments) return 0;

    const ownEntries = Object.values(secondAssignments).filter(
      (a) => a.assignedBy === childUserPersonalNumber
    );

    if (!isSerial) {
      return ownEntries.reduce((sum, a) => sum + (a.quantity || 0), 0);
    }
    return ownEntries.length > 0 ? 1 : 0;
  }

  const isChildUserUnit = userRole === "childUserUnit";

  // Unit-wide child: org first+second when child has a first slice; else own second only;
  // serial rows without first show "free" until this child signs second (assignedBy).
  if (isChildUserUnit && childUserPersonalNumber) {
    const firstAssignment = getFirstAssignmentEntryForChild(
      item,
      childUserPersonalNumber,
    );
    if (firstAssignment) {
      const secondAssignments = item.assignments?.second;
      if (!secondAssignments) return 0;

      const ownEntries = Object.values(secondAssignments).filter((a) =>
        assignerMatchesChild(a?.assignedBy, childUserPersonalNumber),
      );

      if (!isSerial) {
        return ownEntries.reduce((sum, a) => sum + (a.quantity || 0), 0);
      }
      return ownEntries.length > 0 ? 1 : 0;
    }
    const own = getOwnSecondAssignments(item, childUserPersonalNumber);
    if (!isSerial) {
      return own.reduce((sum, a) => sum + (a.quantity || 0), 0);
    }
    return own.length > 0 ? 1 : 0;
  }

  // For admin/parentsUser: standard calculation
  if (!isSerial) {
    const quantity = item.quantity || 0;
    const availableQuantity = item.availableQuantity ?? quantity;
    return quantity - availableQuantity;
  }

  // Serial items: status-based
  return item.status === "assigned" ? 1 : 0;
};

/**
 * Calculate available quantity for an inventory item
 * For non-serial items: use availableQuantity field directly
 * For serial items: use status-based (1 if available, 0 if assigned)
 * @param {Object} item - Inventory item
 * @param {string} userRole - admin | parentsUser | childUser | childUserUnit
 * @param {string} childUserPersonalNumber - Personal number of childUser (if applicable)
 * @returns {number} Available quantity
 */
const calculateAvailableQuantity = (
  item,
  userRole = "admin",
  childUserPersonalNumber = null
) => {
  if (!item) return 0;

  const isChildUser = userRole === "childUser";
  const isSerial = isSerialItem(item);

  // For childUser: first allocation minus their own second assignments
  if (isChildUser && childUserPersonalNumber) {
    const firstAssignment = item.assignments?.first?.[childUserPersonalNumber];
    if (!firstAssignment) return 0;

    const firstQuantity = firstAssignment.quantity || 0;
    const secondAssignments = item.assignments?.second;

    if (!isSerial) {
      if (!secondAssignments) return firstQuantity;
      const ownSecondSum = Object.values(secondAssignments)
        .filter((a) => a.assignedBy === childUserPersonalNumber)
        .reduce((sum, a) => sum + (a.quantity || 0), 0);
      return Math.max(0, firstQuantity - ownSecondSum);
    }
    // For serial items: available if no own entries in second
    const ownEntries = Object.values(secondAssignments || {}).filter(
      (a) => a.assignedBy === childUserPersonalNumber
    );
    return ownEntries.length > 0 ? 0 : 1;
  }

  const isChildUserUnit = userRole === "childUserUnit";

  if (isChildUserUnit && childUserPersonalNumber) {
    const firstAssignment = getFirstAssignmentEntryForChild(
      item,
      childUserPersonalNumber,
    );
    if (firstAssignment) {
      const firstQuantity = firstAssignment.quantity || 0;
      const secondAssignments = item.assignments?.second;

      if (!isSerial) {
        if (!secondAssignments) return firstQuantity;
        const ownSecondSum = Object.values(secondAssignments)
          .filter((a) =>
            assignerMatchesChild(a?.assignedBy, childUserPersonalNumber),
          )
          .reduce((sum, a) => sum + (a.quantity || 0), 0);
        return Math.max(0, firstQuantity - ownSecondSum);
      }
      const ownEntries = Object.values(secondAssignments || {}).filter((a) =>
        assignerMatchesChild(a?.assignedBy, childUserPersonalNumber),
      );
      return ownEntries.length > 0 ? 0 : 1;
    }
    if (!isSerial) {
      return item.availableQuantity ?? item.quantity ?? 0;
    }
    if (item.status === "returned") return 0;
    const own = getOwnSecondAssignments(item, childUserPersonalNumber);
    return own.length > 0 ? 0 : 1;
  }

  // For admin/parentsUser: standard calculation
  if (!isSerial) {
    return item.availableQuantity ?? item.quantity ?? 0;
  }

  // Serial items: status-based
  return item.status === "available" ? 1 : 0;
};

/**
 * Determine the correct status for an inventory item
 * For non-serial items: use availableQuantity to determine status
 * For serial items: use existing status
 * @param {Object} item - Inventory item
 * @param {string} userRole - admin | parentsUser | childUser | childUserUnit
 * @param {string} childUserPersonalNumber - Personal number of childUser (if applicable)
 * @returns {string} Status (available, assigned, returned)
 */
const calculateItemStatus = (
  item,
  userRole = "admin",
  childUserPersonalNumber = null
) => {
  if (!item) return "available";

  // If item has returned status, keep it
  if (item.status === "returned") return "returned";

  const isChildUser = userRole === "childUser";
  const isSerial = isSerialItem(item);

  // For childUser: status is determined ONLY by assignments.second
  // assignments.first = items assigned TO childUser by admin
  // assignments.second = items assigned BY childUser to others
  // Status logic: if assignments.second exists and has entries, item is "assigned", otherwise "available"
  if (isChildUser && childUserPersonalNumber) {
    const firstAssignment = item.assignments?.first?.[childUserPersonalNumber];
    if (!firstAssignment) return "available";

    const secondAssignments = item.assignments?.second || {};
    const ownEntries = Object.values(secondAssignments).filter(
      (a) => a.assignedBy === childUserPersonalNumber
    );

    if (!isSerial) {
      if (ownEntries.length === 0) return "available";
      const firstQuantity = firstAssignment.quantity || 0;
      const ownSecondSum = ownEntries.reduce(
        (sum, a) => sum + (a.quantity || 0),
        0
      );
      return ownSecondSum >= firstQuantity ? "assigned" : "available";
    }
    return ownEntries.length > 0 ? "assigned" : "available";
  }

  const isChildUserUnit = userRole === "childUserUnit";

  if (isChildUserUnit && childUserPersonalNumber) {
    const firstAssignment = getFirstAssignmentEntryForChild(
      item,
      childUserPersonalNumber,
    );
    if (firstAssignment) {
      const secondAssignments = item.assignments?.second || {};
      const ownEntries = Object.values(secondAssignments).filter((a) =>
        assignerMatchesChild(a?.assignedBy, childUserPersonalNumber),
      );

      if (!isSerial) {
        if (ownEntries.length === 0) return "available";
        const firstQuantity = firstAssignment.quantity || 0;
        const ownSecondSum = ownEntries.reduce(
          (sum, a) => sum + (a.quantity || 0),
          0,
        );
        return ownSecondSum >= firstQuantity ? "assigned" : "available";
      }
      return ownEntries.length > 0 ? "assigned" : "available";
    }
    if (!isSerial) {
      const availableQuantity = item.availableQuantity ?? item.quantity ?? 0;
      return availableQuantity === 0 ? "assigned" : "available";
    }
    const own = getOwnSecondAssignments(item, childUserPersonalNumber);
    return own.length > 0 ? "assigned" : "available";
  }

  // For admin/parentsUser: standard calculation
  if (!isSerial) {
    const availableQuantity = item.availableQuantity ?? item.quantity ?? 0;
    return availableQuantity === 0 ? "assigned" : "available";
  }

  // Serial items: use existing status
  return item.status || "available";
};

/**
 * Get assigned personnel information for an inventory item
 * @param {Object} item - Inventory item
 * @param {string} userRole - admin | parentsUser | childUser | childUserUnit
 * @returns {Array} Array of assigned personnel info
 */
const getAssignedPersonnel = (
  item,
  userRole = "admin",
  childUserPersonalNumber = null
) => {
  if (!item || !item.assignments) return [];

  const personnel = [];

  // Unit-wide child: חתום ל only after this child second-signs (assignedBy); else empty for resign flow
  if (userRole === "childUserUnit" && childUserPersonalNumber) {
    const sec = item.assignments?.second || {};
    const delegatedPairs = Object.entries(sec).filter(([, assignment]) =>
      assignerMatchesChild(
        assignment?.assignedBy,
        childUserPersonalNumber,
      ),
    );
    if (delegatedPairs.length > 0) {
      delegatedPairs.forEach(([personalNumber, assignment]) => {
        personnel.push({
          personalNumber,
          quantity: assignment.quantity || 0,
          assignedAt: assignment.assignedAt,
          firstName: assignment.firstName || "",
          lastName: assignment.lastName || "",
          assignedBy: assignment.assignedBy || "",
          note: assignment.note || "",
        });
      });
    }
    return personnel;
  }

  // For childUser: only show their own entries in assignments.second
  if (userRole === "childUser") {
    if (item.assignments.second) {
      Object.entries(item.assignments.second).forEach(
        ([personalNumber, assignment]) => {
          if (
            !childUserPersonalNumber ||
            assignment.assignedBy === childUserPersonalNumber
          ) {
            personnel.push({
              personalNumber,
              quantity: assignment.quantity || 0,
              assignedAt: assignment.assignedAt,
              firstName: assignment.firstName || "",
              lastName: assignment.lastName || "",
              assignedBy: assignment.assignedBy || "",
              note: assignment.note || "",
            });
          }
        }
      );
    }
    return personnel;
  }

  // For admin and parentsUser: show assignments.first (admin/parentsUser assignments)
  if (item.assignments.first) {
    Object.entries(item.assignments.first).forEach(
      ([personalNumber, assignment]) => {
        personnel.push({
          personalNumber,
          quantity: assignment.quantity || 0,
          assignedAt: assignment.assignedAt,
          firstName: assignment.firstName || "",
          lastName: assignment.lastName || "",
          assignedBy: assignment.assignedBy || "",
          note: assignment.note || "",
        });
      }
    );
  }

  return personnel;
};

/**
 * Custom hook for efficient inventory calculations
 * Pre-calculates all quantities once and memoizes the result
 * @param {Array} inventory - Array of inventory items
 * @param {string} userRole - admin | parentsUser | childUser | childUserUnit
 * @param {string} childUserPersonalNumber - Personal number of childUser (if applicable)
 * @returns {Array} Enhanced inventory items with calculated quantities
 */
export const useInventoryCalculations = (
  inventory,
  userRole = "admin",
  childUserPersonalNumber = null
) => {
  return useMemo(() => {
    if (!inventory || !Array.isArray(inventory)) return [];

    return inventory.map((item) => {
      const assignedQuantity = calculateAssignedQuantity(
        item,
        userRole,
        childUserPersonalNumber
      );
      const availableQuantity = calculateAvailableQuantity(
        item,
        userRole,
        childUserPersonalNumber
      );
      const calculatedStatus = calculateItemStatus(
        item,
        userRole,
        childUserPersonalNumber
      );
      const assignedPersonnel = getAssignedPersonnel(
        item,
        userRole,
        childUserPersonalNumber
      );

      // childUser / childUserUnit: display quantity from child's first slice when present
      let displayQuantity = item.quantity;
      if (
        (userRole === "childUser" || userRole === "childUserUnit") &&
        childUserPersonalNumber
      ) {
        const firstAssignment =
          userRole === "childUserUnit"
            ? getFirstAssignmentEntryForChild(item, childUserPersonalNumber)
            : item.assignments?.first?.[childUserPersonalNumber];
        if (firstAssignment) {
          displayQuantity = firstAssignment?.quantity ?? item.quantity;
        }
      }

      return {
        ...item,
        assignedQuantity,
        availableQuantity,
        calculatedStatus,
        assignedPersonnel,
        displayQuantity, // Use this for display instead of item.quantity for childUser
      };
    });
  }, [inventory, userRole, childUserPersonalNumber]);
};

// Export individual functions for use in other components
export {
  calculateAssignedQuantity,
  calculateAvailableQuantity,
  calculateItemStatus,
  getAssignedPersonnel,
};
